import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Clock } from 'lucide-react';

/**
 * Satın alma talebi denetim geçmişi.
 *
 * Onaylanmamış talepler düzeltilip silinebildiği için "kim ne yaptı"nın
 * izlenebilir olması gerekiyor. Kayıtlar talebin kendisinde değil ayrı bir
 * koleksiyonda (`purchase_request_logs`) durur — silinen talep listeden
 * kaybolur ama geçmişi kalır, silinen talebin özeti de kayda yazılır.
 *
 * `requestId` verilirse yalnızca o talebin geçmişi, verilmezse tüm talepler.
 */
const ISLEM_RENGI = {
  olusturuldu: 'bg-[#e8dfd0] text-[#3F3E39]',
  duzenlendi: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  silindi: 'bg-[#7a3535]/10 text-[#7a3535]',
  onaya_gonderildi: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  maliyet_onayi: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  yonetici_onayi: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  reddedildi: 'bg-[#7a3535]/10 text-[#7a3535]',
  siparise_donusturuldu: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  siparis_silindi: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
};

const zaman = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? '-' : d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function TalepGecmisiModal({ open, onOpenChange, requestId = null }) {
  const [kayitlar, setKayitlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');

  useEffect(() => {
    if (!open) return;
    setYukleniyor(true);
    setHata('');
    api.get('/purchase-request-logs', { params: requestId ? { request_id: requestId } : {} })
      .then(r => setKayitlar(r.data || []))
      .catch(() => setHata('Geçmiş yüklenemedi'))
      .finally(() => setYukleniyor(false));
  }, [open, requestId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#faf7f2] border-[#d0c4b0]">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] flex items-center gap-2">
            <Clock size={18} /> {requestId ? 'Talep Geçmişi' : 'Talep İşlem Geçmişi'}
          </DialogTitle>
          <DialogDescription className="text-[#8a7e70]">
            {requestId
              ? 'Bu talep üzerinde yapılan tüm işlemler.'
              : 'Tüm taleplerde kim ne yaptı — silinen talepler dâhil.'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1" data-testid="talep-gecmisi-liste">
          {yukleniyor && (
            <div className="flex items-center gap-2 py-8 justify-center text-[#8a7e70]">
              <Loader2 size={16} className="animate-spin" /> Yükleniyor
            </div>
          )}
          {!yukleniyor && hata && (
            <div className="py-8 text-center text-[#7a3535]">{hata}</div>
          )}
          {!yukleniyor && !hata && kayitlar.length === 0 && (
            <div className="py-8 text-center text-[#8a7e70]">Henüz kayıt yok</div>
          )}

          {!yukleniyor && kayitlar.map((k) => (
            <div key={k.id} className="border-b border-[#e3dbcd] py-3 last:border-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className={`status-badge ${ISLEM_RENGI[k.islem] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                    {k.ozet}
                  </span>
                  <div className="mt-1 text-sm text-[#3F3E39]">
                    <span className="font-semibold">{k.user_name}</span>
                    <span className="text-[#8a7e70]"> · {zaman(k.at)}</span>
                  </div>
                </div>
                {/* Silinen talep artık listede yok; hangi talep olduğu burada kalsın */}
                {k.talep_ozeti && (
                  <div className="shrink-0 text-right text-xs text-[#8a7e70]">
                    <div>{k.talep_ozeti.kalem_sayisi} kalem</div>
                    {k.talep_ozeti.total_estimated_cost != null && (
                      <div className="font-mono">
                        {Number(k.talep_ozeti.total_estimated_cost).toFixed(2)} TL
                      </div>
                    )}
                  </div>
                )}
              </div>

              {k.degisiklikler?.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-[#3F3E39]">
                  {k.degisiklikler.map((d, i) => (
                    <li key={i} className="flex flex-wrap gap-1">
                      <span className="text-[#8a7e70]">{d.alan}:</span>
                      {d.eski != null && d.eski !== '' && (
                        <span className="line-through text-[#8a7e70]">{String(d.eski)}</span>
                      )}
                      {d.yeni != null && d.yeni !== '' && (
                        <span className="font-medium">{String(d.yeni)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {k.islem === 'silindi' && k.talep_ozeti?.urunler?.length > 0 && (
                <div className="mt-2 text-xs text-[#8a7e70]">
                  Silinen kalemler: {k.talep_ozeti.urunler.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
